import output from "_src/engine/output";
import events from "_src/engine/events";
import chunksBuilder from "_src/engine/data_builders/chunks_builder";

import {objectType} from "_src/constants";
import appState from "_src/app_state";
import config from "_src/config";

const _needOctreeUpdate = new WeakSet();
const _needNormalUpdate = new WeakSet();
const _needHalfEdgesUpdate = new WeakSet();

const _objectsUpdatePromises = new Map();

const OS_OCTREE      = 0b0000001;
const OS_HALF_EDGE   = 0b0000010;
const OS_BOUNDING    = 0b0000100;
const OS_NORMAL      = 0b0001000;

export default {
  OS_OCTREE,
  OS_HALF_EDGE,
  OS_BOUNDING,
  OS_NORMAL,

  init() {
    events.on("update_chunk_octree", (chunk) => {
      setNeedsUpdate(chunk, OS_OCTREE, true);
    });
    events.on("update_chunk_halfedges", (chunk) => {
      setNeedsUpdate(chunk, OS_HALF_EDGE, true);
    });
  },

  update(object) {

    if (_objectsUpdatePromises.has(object)) {
      return _objectsUpdatePromises.get(object);
    }

    if (!object.loaded) {
      output.error(output.DEVELOPERS_ERROR, "Couldn't update unloaded object " + object.getID());
      return Promise.reject("Object update error.");
    }

    let update = 0b0;

    const promise = Promise.resolve().then(() => {
      const hasNormals = object.type === objectType.SURFELS;
      const needs = (appState.getState(appState.GS_NORMALS) || appState.getStateValue("renderMode") === appState.RM_POINTS) && !hasNormals;
      const needsAverage = object.type === objectType.FUSION || object.type === objectType.CAD;
      const searchInChunk = object.type !== objectType.CAD;

      update |= OS_NORMAL;

      return updateNormals(object, needs, needsAverage, searchInChunk).then(() => { object.setState(OS_NORMAL, object.getState(OS_NORMAL) || needs || hasNormals); });
    }).then(() => {
      if (!object.getState(OS_BOUNDING)) {
        update |= OS_BOUNDING;
        return updateBoundings(object).then(() => { object.setState(OS_BOUNDING, true); });
      }
      return Promise.resolve();
    }).then(() => {
      const needs = appState.getState(appState.GS_OCTREE);
      update |= OS_OCTREE;
      return updateOctree(object, needs).then(() => { object.setState(OS_OCTREE, needs); });
    }).then(() => {
      const needs = appState.getState(appState.GS_HALF_EDGE);
      update |= OS_HALF_EDGE;
      return updateHalfEdges(object, needs).then(() => { object.setState(OS_HALF_EDGE, needs); });
    }).then(() => {
      _objectsUpdatePromises.delete(object);
      //NOTE: it doesn't work for CAD objects
      const isUpToDate = (!appState.getState(appState.GS_NORMALS) || appState.getState(appState.GS_NORMALS) && object.getState(OS_NORMAL)) &&
                         (!appState.getState(appState.GS_OCTREE)  || appState.getState(appState.GS_OCTREE) && object.getState(OS_OCTREE)) &&
                         (!appState.getState(appState.GS_HALF_EDGE) || appState.getState(appState.GS_HALF_EDGE) && object.getState(OS_HALF_EDGE)) &&
                         object.getState(OS_BOUNDING);
      finishUpdate(object, update);
      return isUpToDate ? Promise.resolve() : this.update(object);
    });

    _objectsUpdatePromises.set(object, promise);

    return promise;

  },

  checkIsUpdating(object) {
    return _objectsUpdatePromises.has(object);
  },

  getUpdatePromise(object) {
    return _objectsUpdatePromises.get(object);
  }

}

function updateOctree(object, needs) {

  const shouldBuild = !object.getState(OS_OCTREE);

  const promises = [];
  for (const frame of object.frames) {
    if (!frame.loaded) {
      continue;
    }

    for (const chunk of frame.model.chunks) {
      if (!chunk.geometry) {
        // NOTE: this can happen if the chunk is unloaded during update
        continue;
      }

      if (!needs) {
        chunk.geometry.removeOctree();
        chunk.updateSelectionMask(false);
        setNeedsUpdate(chunk, OS_OCTREE, true);
        continue;
      }

      if (!getNeedsUpdate(chunk, OS_OCTREE) && !shouldBuild) {
        continue;
      }

      promises.push(chunk.geometry.buildOctree(config.useWorkers).then(() => {
        setNeedsUpdate(chunk, OS_OCTREE, false);
        return chunk;
      }));
    }
  }

  return Promise.all(promises).then((chunks) => {
    for (const chunk of chunks) {
      chunk.updateSelectionMask(true);
    }
  });
}

function updateBoundings(object) {
  return object.recalcWorldMinmax();
}

function updateNormals(object, needs, needsDuplis, searchInChunk) {

  const shouldBuild = !object.getState(OS_NORMAL);

  const promises = [];
  for (const frame of object.frames) {
    if (!frame.loaded) {
      continue;
    }

    const updates = [];

    for (const chunk of frame.model.chunks) {

      if (!needs) {
        // chunk.removeNormals();
        continue;
      }

      if (!getNeedsUpdate(chunk, OS_NORMAL) && !shouldBuild) {
        continue;
      }
      updates.push(chunk.buildNormalsAndBorder(config.useWorkers, true, needsDuplis).then(() => {
        return chunk;
      }));
    }

    if (updates.length == 0) {
      continue;
    }

    promises.push(Promise.all(updates).then((chunks) => {
      if (needsDuplis) {
        const chunkToDuplis = new Map();
        chunksBuilder.buildDupliVertices(frame.model.chunks, chunkToDuplis, searchInChunk);
        for (const chunk of chunks) {
          chunk.averageNormals(chunkToDuplis.get(chunk));
          setNeedsUpdate(chunk, OS_NORMAL, false);
        }
      }
    }));
  }

  return Promise.all(promises);
}

function updateHalfEdges(object, needs) {

  const shouldBuild = !object.getState(OS_HALF_EDGE);

  const promises = [];
  for (const frame of object.frames) {
    if (!frame.loaded) {
      continue;
    }

    const updates = [];
    for (const chunk of frame.model.chunks) {
      if (!chunk.geometry) {
        // NOTE: this can happen if the chunk is unloaded during update
        continue;
      }

      if (!needs) {
        chunk.geometry.removeHalfEdges();
        chunk.geometry.deleteVerticesBorderMask();
        chunk.clearParentVerticesMask();
        setNeedsUpdate(chunk, OS_HALF_EDGE, true);
        continue;
      }

      if (!getNeedsUpdate(chunk, OS_HALF_EDGE) && !shouldBuild) {
        continue;
      }

      chunk.resetDupliVertices();
      chunk.resetParentVerticesMask();

      updates.push(chunk.geometry.buildHalfEdge(config.useWorkers).then(() => {

        chunk.resetDupliVertices();
        chunk.resetParentVerticesMask();

        setNeedsUpdate(chunk, OS_HALF_EDGE, false);
        return chunk;
      }));
    }
    promises.push(Promise.all(updates).then((chunks) => {
      const chunkToDuplis = new Map();
      chunksBuilder.buildDupliVertices(chunks, chunkToDuplis);
      for (const chunk of chunks) {
        chunksBuilder.addDupliVerticesToChunk(chunk, chunkToDuplis.get(chunk));
      }
    }));
  }

  return Promise.all(promises);
}

function setNeedsUpdate(chunk, state, needs) {
  const container = getContainer(state);
  if (container) {
    if (needs) {
      container.add(chunk);
    } else {
      container.delete(chunk);
    }
  }
}

function getNeedsUpdate(chunk, state) {
  const container = getContainer(state);
  return container ? container.has(chunk) : false;
}

function getContainer(state) {
  if (state == OS_HALF_EDGE) {
    return _needHalfEdgesUpdate;
  }
  if (state == OS_OCTREE) {
    return _needOctreeUpdate;
  }
  if (state == OS_NORMAL) {
    return _needNormalUpdate;
  }
  return null;
}


function finishUpdate(object, state) {
  events.emit("object_geometry_updated", { object, state });
}
